import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { toast } from 'react-toastify';
import { X, Plus } from 'lucide-react';
import { formatUserDisplayName, displayGroupRole } from '../utils/userDisplayName';
import { formatDateShortRu } from '../utils/dateUtils';
import { useConfig } from '../contexts/ConfigContext';
import { getDirectionTerms } from '../utils/organizationHelpers';
import { useAuth } from '../contexts/AuthContext';
import ScheduleForm from './ScheduleForm';
import './GroupForm.css';

function GroupSchedulePanel({ groupId, onAdd, onEdit, onChanged }) {
    const { data: schedule, isLoading, refetch } = useQuery(
        ['groupSchedule', groupId],
        async () => {
            const response = await axios.get(`${API_BASE_URL}/schedule/group/${groupId}`, {
                withCredentials: true
            });
            return response.data;
        },
        { enabled: !!groupId }
    );

    const handleDelete = async (eventId) => {
        if (!window.confirm('Удалить эту встречу?')) return;
        try {
            await axios.delete(`${API_BASE_URL}/schedule/${eventId}`, { withCredentials: true });
            toast.success('Встреча удалена');
            refetch();
            onChanged?.();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка удаления');
        }
    };

    if (isLoading) {
        return <div className="loading"><div className="spinner"></div></div>;
    }

    return (
        <div className="ge-meetings">
            <div className="ge-meetings-head">
                <h4>Встречи</h4>
                <button type="button" className="btn btn-primary btn-sm" onClick={onAdd}>
                    <Plus size={16} />
                    Добавить встречу
                </button>
            </div>
            {!schedule?.length ? (
                <div className="ge-empty">
                    <p>Встреч пока нет. Добавьте цикл: даты, тему и тренеров.</p>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={onAdd}>
                        Добавить первую встречу
                    </button>
                </div>
            ) : (
                <ul className="ge-meeting-list">
                    {schedule.map((event) => (
                        <li key={event.id} className="ge-meeting">
                            <div className="ge-meeting-main">
                                <span className="ge-cycle">Цикл {event.cycle}</span>
                                <strong>{event.theme}</strong>
                                <span className="ge-meeting-dates">
                                    {formatDateShortRu(event.start_date)} — {formatDateShortRu(event.end_date)}
                                </span>
                                {event.trainers?.length > 0 && (
                                    <span className="ge-meeting-trainers">
                                        {event.trainers.map((t) => formatUserDisplayName(t)).join(', ')}
                                    </span>
                                )}
                            </div>
                            <div className="ge-meeting-actions">
                                <button type="button" className="btn btn-secondary btn-sm" onClick={() => onEdit(event)}>
                                    Изменить
                                </button>
                                <button type="button" className="btn-icon" onClick={() => handleDelete(event.id)} title="Удалить встречу">
                                    <X size={16} />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

const HIDDEN_NEW_MODULE_SHORTS = new Set([
    'ГТ-М3', 'ГТ-М4', 'ГТ-М5', 'ГТ-М6', 'ГТ-М8',
    'УГТ-М2', 'УГТ-М3', 'УГТ-М4',
    'ПД-М3', 'ПД-М4', 'ПД-М5', 'ПД-М6', 'ПД-М7', 'ПД-АТ'
]);

const GroupForm = ({ group, onSuccess, onCancel, initialTab = 'main', onScheduleChange }) => {
    const { config } = useConfig();
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const isAdmin = user?.role === 'admin';
    const isMag = config?.code === 'mag';
    const isNasledie = config?.code === 'nasledie';
    const directionTerms = getDirectionTerms(config?.code);
    const [loading, setLoading] = useState(false);
    const [regions, setRegions] = useState([]);
    const [directions, setDirections] = useState([]);
    const [users, setUsers] = useState([]);
    const [managerUsers, setManagerUsers] = useState([]);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [showEventForm, setShowEventForm] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [zoomInfo, setZoomInfo] = useState(null);
    const [zoomLoading, setZoomLoading] = useState(false);
    const [zoomRequesting, setZoomRequesting] = useState(false);
    const [signupMailImageFile, setSignupMailImageFile] = useState(null);
    const [signupMailNewFiles, setSignupMailNewFiles] = useState([]);

    const [formData, setFormData] = useState({
        name: '',
        nomination: '',
        direction_id: '',
        region_id: '',
        price_per_cycle: '',
        price_full_course: '',
        recruitment_status: 'open',
        alternative_form: '',
        about: '',
        format_data: [{ weekDay: '', timeFrom: '', timeTo: '', frequency: '' }],
        access_data: [],
        hide_manager_from_trainers: false,
        signup_mail_subject: '',
        signup_mail_text: '',
        signup_mail_image_url: '',
        signup_mail_files: []
    });

    useEffect(() => {
        loadDictionaries();
        if (group) {
            setFormData({
                name: group.name || '',
                nomination: group.nomination || '',
                direction_id: group.direction_id || '',
                region_id: group.region_id || '',
                price_per_cycle: group.price_per_cycle || '',
                price_full_course: group.price_full_course || '',
                recruitment_status: group.recruitment_status || 'open',
                alternative_form: group.alternative_form || '',
                about: group.about || '',
                format_data: group.format_data && group.format_data.length > 0
                    ? group.format_data.map(f => ({ ...f, frequency: f.frequency || '' }))
                    : [{ weekDay: '', timeFrom: '', timeTo: '', frequency: '' }],
                access_data: (group.access_data || []).map((access) => ({
                    ...access,
                    role: displayGroupRole(access.role) || access.role
                })),
                manager_id: group.manager_id ?? '',
                hide_manager_from_trainers: Boolean(group.hide_manager_from_trainers),
                signup_mail_subject: group.signup_mail_subject || '',
                signup_mail_text: group.signup_mail_text || '',
                signup_mail_image_url: group.signup_mail_image_url || '',
                signup_mail_files: Array.isArray(group.signup_mail_files) ? group.signup_mail_files : []
            });
            setSignupMailImageFile(null);
            setSignupMailNewFiles([]);
        } else {
            setFormData({
                name: '',
                nomination: '',
                direction_id: '',
                region_id: '',
                price_per_cycle: '',
                price_full_course: '',
                recruitment_status: 'open',
                alternative_form: '',
                about: '',
                format_data: [{ weekDay: '', timeFrom: '', timeTo: '', frequency: '' }],
                access_data: [],
                hide_manager_from_trainers: false,
                signup_mail_subject: '',
                signup_mail_text: '',
                signup_mail_image_url: '',
                signup_mail_files: []
            });
            setSignupMailImageFile(null);
            setSignupMailNewFiles([]);
        }
    }, [group]);

    useEffect(() => {
        if (!isAdmin) return undefined;
        let cancelled = false;
        axios.get(`${API_BASE_URL}/users/trainers?all=1`, { withCredentials: true })
            .then((res) => { if (!cancelled) setManagerUsers(res.data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [isAdmin]);

    useEffect(() => {
        setActiveTab(initialTab || 'main');
        setShowEventForm(false);
        setEditingEvent(null);
    }, [group?.id, initialTab]);

    useEffect(() => {
        if (!isNasledie || !group?.id) {
            setZoomInfo(null);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setZoomLoading(true);
            try {
                const res = await axios.get(`${API_BASE_URL}/groups/${group.id}/zoom`, {
                    withCredentials: true
                });
                if (!cancelled) setZoomInfo(res.data);
            } catch (e) {
                if (!cancelled) setZoomInfo(null);
            } finally {
                if (!cancelled) setZoomLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isNasledie, group?.id]);

    const loadDictionaries = async () => {
        try {
            const [regionsRes, directionsRes, usersRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/regions/public`),
                axios.get(`${API_BASE_URL}/directions/public`),
                axios.get(`${API_BASE_URL}/users/trainers`, { withCredentials: true })
            ]);
            setRegions(regionsRes.data);
            setDirections(directionsRes.data);
            setUsers(usersRes.data);
        } catch (error) {
            console.error('Ошибка загрузки справочников:', error);
            toast.error('Ошибка загрузки данных');
        }
    };

    const isTherapeuticOrSupervisory = () => {
        if (!formData.direction_id) return false;
        const direction = directions.find(d => d.id == formData.direction_id);
        if (!direction) return false;

        const name = (direction.name || '').toLowerCase();
        const fullName = (direction.full_name || '').toLowerCase();
        const combined = `${name} ${fullName}`;

        return combined.includes('терапевтическая') || combined.includes('супервизорская');
    };

    const peopleOptions = useMemo(() => {
        const byId = new Map();
        for (const user of users || []) {
            byId.set(String(user.id), user);
        }
        for (const access of formData.access_data || []) {
            if (access.user_id && !byId.has(String(access.user_id))) {
                byId.set(String(access.user_id), {
                    id: access.user_id,
                    name: access.name || '',
                    email: ''
                });
            }
        }
        if (group?.manager_id && !byId.has(String(group.manager_id))) {
            byId.set(String(group.manager_id), {
                id: group.manager_id,
                name: group.manager_name || '',
                email: ''
            });
        }
        return [...byId.values()].sort((a, b) =>
            formatUserDisplayName(a).localeCompare(formatUserDisplayName(b), 'ru', { sensitivity: 'base' })
        );
    }, [users, formData.access_data, group]);

    const managerOptions = useMemo(() => {
        const byId = new Map();
        for (const user of managerUsers.length ? managerUsers : users) {
            byId.set(String(user.id), user);
        }
        if (group?.manager_id && !byId.has(String(group.manager_id))) {
            byId.set(String(group.manager_id), {
                id: group.manager_id,
                name: group.manager_name || '',
                email: ''
            });
        }
        return [...byId.values()].sort((a, b) =>
            formatUserDisplayName(a).localeCompare(formatUserDisplayName(b), 'ru', { sensitivity: 'base' })
        );
    }, [managerUsers, users, group]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'manager_id') {
            setFormData(prev => ({
                ...prev,
                manager_id: value === '' ? '' : parseInt(value, 10)
            }));
            return;
        }
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFormatChange = (index, field, value) => {
        const newFormats = [...formData.format_data];
        newFormats[index][field] = value;
        setFormData(prev => ({ ...prev, format_data: newFormats }));
    };

    /** Частота для всей группы — один выбор, применяется ко всем слотам формата */
    const groupFormatFrequency = formData.format_data[0]?.frequency ?? '';

    const handleGroupFormatFrequencyChange = (value) => {
        const newFormats = formData.format_data.map(f => ({ ...f, frequency: value }));
        setFormData(prev => ({ ...prev, format_data: newFormats }));
    };

    const addFormat = () => {
        const frequency = formData.format_data[0]?.frequency ?? '';
        setFormData(prev => ({
            ...prev,
            format_data: [...prev.format_data, { weekDay: '', timeFrom: '', timeTo: '', frequency }]
        }));
    };

    const removeFormat = (index) => {
        setFormData(prev => ({
            ...prev,
            format_data: prev.format_data.filter((_, i) => i !== index)
        }));
    };

    const handleAccessChange = (index, field, value) => {
        const newAccess = [...formData.access_data];
        newAccess[index][field] = field === 'user_id' ? parseInt(value) : value;

        // Если изменился user_id, обновляем имя и фото
        if (field === 'user_id') {
            const user = users.find(u => u.id == value);
            if (user) {
                newAccess[index].name = formatUserDisplayName(user);
                newAccess[index].photo_url = user.photo_url || '';
            }
        }

        setFormData(prev => ({ ...prev, access_data: newAccess }));
    };

    const addAccess = () => {
        setFormData(prev => ({
            ...prev,
            access_data: [...prev.access_data, {
                user_id: '',
                name: '',
                photo_url: '',
                role: 'Ассистент',
                can_edit: false,
                notifications: false
            }]
        }));
    };

    const removeAccess = (index) => {
        setFormData(prev => ({
            ...prev,
            access_data: prev.access_data.filter((_, i) => i !== index)
        }));
    };

    const validateForm = () => {
        const missing = [];

        if (!formData.direction_id) missing.push(directionTerms.one);
        if (!formData.region_id) missing.push('Регион');
        if (isMag && isTherapeuticOrSupervisory() && !formData.nomination.trim()) {
            missing.push('Название');
        }

        if (missing.length > 0) {
            if (activeTab !== 'main') setActiveTab('main');
            toast.error(`Заполните обязательные поля: ${missing.join(', ')}`);
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);

        try {
            const url = group
                ? `${API_BASE_URL}/groups/${group.id}`
                : `${API_BASE_URL}/groups`;

            const method = group ? 'put' : 'post';

            let signupMailImageUrl = formData.signup_mail_image_url || '';
            let signupMailFiles = Array.isArray(formData.signup_mail_files) ? formData.signup_mail_files : [];
            if (isNasledie && signupMailImageFile) {
                const body = new FormData();
                body.append('image', signupMailImageFile);
                const uploaded = await axios.post(`${API_BASE_URL}/groups/signup-mail-image`, body, {
                    withCredentials: true
                });
                signupMailImageUrl = uploaded.data.url;
            }
            if (isNasledie && signupMailNewFiles.length) {
                const body = new FormData();
                signupMailNewFiles.forEach((file) => body.append('files', file));
                const uploaded = await axios.post(`${API_BASE_URL}/groups/signup-mail-files`, body, {
                    withCredentials: true
                });
                signupMailFiles = signupMailFiles.concat(uploaded.data.files || []);
            }

            const payload = {
                ...formData,
                direction_id: parseInt(formData.direction_id, 10),
                region_id: parseInt(formData.region_id, 10),
                signup_mail_image_url: isNasledie ? signupMailImageUrl : undefined,
                signup_mail_files: isNasledie ? signupMailFiles : undefined
            };
            if (!isNasledie) {
                delete payload.signup_mail_image_url;
                delete payload.signup_mail_files;
            }
            if (!group || !isAdmin) {
                delete payload.manager_id;
            }

            await axios[method](url, payload, { withCredentials: true });

            toast.success(group ? 'Группа обновлена' : 'Группа создана');
            onSuccess();
        } catch (error) {
            console.error('Ошибка сохранения группы:', error);
            const data = error.response?.data;
            const fieldLabels = {
                direction_id: directionTerms.one,
                region_id: 'Регион',
                nomination: 'Название'
            };
            if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
                const msg = data.errors
                    .map((err) => {
                        if (err.msg && err.msg !== 'Invalid value') return err.msg;
                        if (err.path && fieldLabels[err.path]) return `Укажите: ${fieldLabels[err.path]}`;
                        return err.msg || err.message || JSON.stringify(err);
                    })
                    .join('; ');
                toast.error(msg);
            } else {
                toast.error(data?.message || 'Ошибка сохранения');
            }
        } finally {
            setLoading(false);
        }
    };

    const directionName = directions.find((d) => String(d.id) === String(formData.direction_id))?.name;
    const selectableDirections = directions.filter((direction) => (
        String(direction.id) === String(formData.direction_id)
        || !HIDDEN_NEW_MODULE_SHORTS.has(direction.short_name)
    ));
    const regionName = regions.find((r) => String(r.id) === String(formData.region_id))?.name;
    const heading = formData.nomination?.trim() || formData.name?.trim() || (group ? 'Группа' : 'Новая группа');
    const subtitle = [directionName, regionName].filter(Boolean).join(' · ') || 'Заполните основные данные';
    const tabs = [
        ['main', 'Основные данные'],
        ['schedule', 'Расписание'],
        ['access', `Тренеры${formData.access_data.length ? ` (${formData.access_data.length})` : ''}`]
    ];
    if (isNasledie) tabs.push(['mailing', 'Рассылка']);
    if (isNasledie && group?.id) tabs.push(['zoom', 'Zoom']);

    const openTab = (key) => {
        setShowEventForm(false);
        setEditingEvent(null);
        setActiveTab(key);
    };

    const formatBlock = (
        <div className="ge-format">
            <div className="form-group">
                <label>Как часто встречается группа</label>
                <select
                    value={groupFormatFrequency}
                    onChange={(e) => handleGroupFormatFrequencyChange(e.target.value)}
                >
                    <option value="">По расписанию</option>
                    <option value="weekly">Раз в неделю (регулярно)</option>
                    <option value="biweekly">Раз в 2 недели (регулярно)</option>
                </select>
            </div>
            {formData.format_data.map((format, index) => (
                <div key={index} className="format-row">
                    <select
                        value={format.weekDay}
                        onChange={(e) => handleFormatChange(index, 'weekDay', e.target.value)}
                    >
                        <option value="">Выберите день</option>
                        <option value="Понедельник">Понедельник</option>
                        <option value="Вторник">Вторник</option>
                        <option value="Среда">Среда</option>
                        <option value="Четверг">Четверг</option>
                        <option value="Пятница">Пятница</option>
                        <option value="Суббота">Суббота</option>
                        <option value="Воскресенье">Воскресенье</option>
                    </select>
                    <input
                        type="time"
                        aria-label="Время начала"
                        value={format.timeFrom}
                        onChange={(e) => handleFormatChange(index, 'timeFrom', e.target.value)}
                    />
                    <input
                        type="time"
                        aria-label="Время окончания"
                        value={format.timeTo}
                        onChange={(e) => handleFormatChange(index, 'timeTo', e.target.value)}
                    />
                    {formData.format_data.length > 1 && (
                        <button type="button" className="btn-icon" onClick={() => removeFormat(index)} title="Удалить время">
                            <X size={16} />
                        </button>
                    )}
                </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" onClick={addFormat}>
                + Добавить время
            </button>
        </div>
    );

    return (
        <div className="group-edit-form admin-group-editor">
            <header className="ge-identity">
                <span className="ge-avatar">{heading.slice(0, 1).toUpperCase()}</span>
                <div>
                    <h2>{heading}</h2>
                    <p>{subtitle}</p>
                </div>
            </header>
            <nav className="ge-tabs" aria-label="Разделы группы">
                {tabs.map(([key, label]) => (
                    <button
                        type="button"
                        key={key}
                        aria-current={activeTab === key ? 'page' : undefined}
                        className={activeTab === key ? 'active' : ''}
                        onClick={() => openTab(key)}
                    >
                        {label}
                    </button>
                ))}
            </nav>

            <form id="group-edit-form" onSubmit={handleSubmit} className="ge-form">
            {activeTab === 'main' && (
                <section className="ge-section">
                    <h3>Основные данные</h3>
                    <p className="ge-hint">Название, {directionTerms.oneLower}, цены и статус набора. Формат и встречи — на вкладке «Расписание».</p>
                    <div className="form-group">
                        <label>Внутреннее наименование</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Например: Группа №1"
                            disabled={group && group.is_accepted}
                        />
                        {group && group.is_accepted && (
                            <small className="form-help">Внутреннее наименование нельзя изменить после подтверждения</small>
                        )}
                    </div>

                    {group && isAdmin && (
                        <div className="form-group">
                            <label>Организатор группы</label>
                            <select
                                name="manager_id"
                                value={formData.manager_id === '' || formData.manager_id === undefined ? '' : String(formData.manager_id)}
                                onChange={handleChange}
                            >
                                {managerOptions.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {formatUserDisplayName(u)}
                                        {u.email ? ` (${u.email})` : ''}
                                    </option>
                                ))}
                            </select>
                            <small className="form-help">
                                Только администратор может передать группу другому пользователю. Новый организатор должен быть активен.
                            </small>
                        </div>
                    )}

                    <div className="form-group">
                        <label>Название</label>
                        <input
                            type="text"
                            name="nomination"
                            value={formData.nomination}
                            onChange={handleChange}
                            placeholder="Например: Гештальт-терапия. Первая ступень"
                            disabled={isMag && !isTherapeuticOrSupervisory()}
                        />
                        {isMag && !isTherapeuticOrSupervisory() && (
                            <small className="form-help">Название доступно только для терапевтических и супервизорских групп</small>
                        )}
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>{directionTerms.one} *</label>
                            <select
                                name="direction_id"
                                value={formData.direction_id}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Выберите {directionTerms.oneLower}</option>
                                {selectableDirections.map(d => (
                                    <option key={d.id} value={d.id}>{d.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Регион *</label>
                            <select
                                name="region_id"
                                value={formData.region_id}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Выберите регион</option>
                                {regions.map(r => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Цена за цикл (₽)</label>
                            <input
                                type="number"
                                name="price_per_cycle"
                                value={formData.price_per_cycle}
                                onChange={handleChange}
                                min="0"
                            />
                        </div>

                        <div className="form-group">
                            <label>Цена за полный курс (₽)</label>
                            <input
                                type="number"
                                name="price_full_course"
                                value={formData.price_full_course}
                                onChange={handleChange}
                                min="0"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Статус набора</label>
                        <select name="recruitment_status" value={formData.recruitment_status} onChange={handleChange}>
                            <option value="open">Набор открыт</option>
                            <option value="closed">Набор закрыт</option>
                            <option value="started">Группа запущена</option>
                            <option value="started_additional">Группа запущена, донабор</option>
                            <option value="completed">Группа завершена</option>
                            <option value="cancelled">Группа не запустилась</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Альтернативная форма записи (URL)</label>
                        <input
                            type="url"
                            name="alternative_form"
                            value={formData.alternative_form}
                            onChange={handleChange}
                            placeholder="https://example.com/form"
                        />
                    </div>

                    <div className="form-group">
                        <label>О группе</label>
                        <textarea
                            name="about"
                            value={formData.about}
                            onChange={handleChange}
                            placeholder="Текст для блока «О группе» на публичной странице (необязательно)"
                            rows={4}
                        />
                    </div>
                </section>
            )}

            {activeTab === 'schedule' && (
                <section className="ge-section">
                    <h3>Расписание</h3>
                    <p className="ge-hint">Формат группы и список встреч. Регулярный день и время задаются здесь, конкретные циклы — в списке встреч.</p>
                    <div className="ge-card">
                        <h4>Формат группы</h4>
                        {formatBlock}
                    </div>
                    {group?.id && !showEventForm ? (
                        <GroupSchedulePanel
                            groupId={group.id}
                            onAdd={() => {
                                setEditingEvent(null);
                                setShowEventForm(true);
                            }}
                            onEdit={(event) => {
                                setEditingEvent(event);
                                setShowEventForm(true);
                            }}
                            onChanged={onScheduleChange}
                        />
                    ) : group?.id && showEventForm ? (
                        <p className="ge-hint">Форма встречи ниже. День и время группы сохраняются кнопкой «Сохранить», когда вы вернётесь к списку встреч.</p>
                    ) : (
                        <p className="ge-hint">Встречи можно добавить после создания группы.</p>
                    )}
                </section>
            )}

            {activeTab === 'access' && (
                <section className="ge-section">
                <div className="access-section">
                    <h3>Тренеры</h3>
                    <p className="ge-hint">
                        Пользователи с доступом к группе: они смогут смотреть и редактировать группу, расписание и заявки.
                    </p>

                    <label className="checkbox-label" style={{ marginBottom: 16 }}>
                        <input
                            type="checkbox"
                            checked={Boolean(formData.hide_manager_from_trainers)}
                            onChange={(e) => setFormData((prev) => ({
                                ...prev,
                                hide_manager_from_trainers: e.target.checked
                            }))}
                        />
                        <span>Не показывать организатора группы в списке тренеров</span>
                    </label>

                    {formData.access_data.map((access, index) => (
                        <div key={index} className="access-item">
                            <div className="access-header">
                                <h4>Пользователь {index + 1}</h4>
                                <button type="button" className="btn-icon" onClick={() => removeAccess(index)}>
                                    <X size={16} />
                                </button>
                            </div>

                            <div className="form-group">
                                <label>Выберите пользователя *</label>
                                <select
                                    value={access.user_id}
                                    onChange={(e) => handleAccessChange(index, 'user_id', e.target.value)}
                                    required
                                >
                                    <option value="">Выберите пользователя</option>
                                    {peopleOptions.map(u => (
                                        <option key={u.id} value={u.id}>{formatUserDisplayName(u)}{u.email ? ` (${u.email})` : ''}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Роль в группе</label>
                                <select
                                    value={displayGroupRole(access.role) || 'Ассистент'}
                                    onChange={(e) => handleAccessChange(index, 'role', e.target.value)}
                                    required
                                >
                                    <option value="Преподаватель">Преподаватель</option>
                                    <option value="Приглашённый преподаватель">Приглашённый преподаватель</option>
                                    <option value="Ассистент">Ассистент</option>
                                </select>
                            </div>

                            <div className="access-permissions">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={access.can_edit || false}
                                        onChange={(e) => handleAccessChange(index, 'can_edit', e.target.checked)}
                                    />
                                    <span>Может редактировать группу</span>
                                </label>

                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={access.notifications || false}
                                        onChange={(e) => handleAccessChange(index, 'notifications', e.target.checked)}
                                    />
                                    <span>Получать уведомления</span>
                                </label>
                            </div>
                        </div>
                    ))}

                    <button type="button" className="btn btn-primary btn-sm" onClick={addAccess}>
                        + Добавить пользователя
                    </button>
                </div>
                </section>
            )}

            {activeTab === 'mailing' && isNasledie && (
                <section className="ge-section">
                    <h3>Рассылка</h3>
                    <p className="ge-hint">Это письмо уходит человеку сразу после записи. На странице заявки он увидит, что информация отправлена на почту. Если тема или текст пустые, письмо не отправляется.</p>
                    <div className="form-group">
                        <label>Тема письма</label>
                        <input
                            type="text"
                            name="signup_mail_subject"
                            value={formData.signup_mail_subject}
                            onChange={handleChange}
                            maxLength={200}
                            placeholder="Например: Вы записались в группу"
                        />
                    </div>
                    <div className="form-group">
                        <label>Текст письма</label>
                        <textarea
                            name="signup_mail_text"
                            value={formData.signup_mail_text}
                            onChange={handleChange}
                            rows={10}
                            placeholder="Информация для записавшегося. Пустая строка отделяет абзацы."
                        />
                    </div>
                    <div className="form-group">
                        <label>Картинка</label>
                        <input
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={(event) => {
                                const file = event.target.files?.[0];
                                if (!file) {
                                    setSignupMailImageFile(null);
                                    return;
                                }
                                if (file.size > 5 * 1024 * 1024) {
                                    toast.error('Картинка больше 5 МБ');
                                    event.target.value = '';
                                    return;
                                }
                                setSignupMailImageFile(file);
                            }}
                        />
                        <small className="form-help">JPEG, PNG, GIF или WebP, до 5 МБ. Появится в письме под текстом.</small>
                        {signupMailImageFile && <p className="form-help">{signupMailImageFile.name}</p>}
                        {!signupMailImageFile && formData.signup_mail_image_url && (
                            <img
                                src={formData.signup_mail_image_url}
                                alt=""
                                style={{ display: 'block', maxWidth: '100%', maxHeight: 180, marginTop: 8, borderRadius: 8 }}
                            />
                        )}
                        {(signupMailImageFile || formData.signup_mail_image_url) && (
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                style={{ marginTop: 8 }}
                                onClick={() => {
                                    setSignupMailImageFile(null);
                                    setFormData((prev) => ({ ...prev, signup_mail_image_url: '' }));
                                }}
                            >
                                Убрать картинку
                            </button>
                        )}
                    </div>
                    <div className="form-group">
                        <label>Файлы</label>
                        <input
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.7z,.txt,.csv,.rtf,.odt,.ods,.odp"
                            onChange={(event) => {
                                const picked = Array.from(event.target.files || []);
                                event.target.value = '';
                                const allowed = picked.filter((file) => file.size <= 10 * 1024 * 1024);
                                if (allowed.length !== picked.length) toast.error('Файл больше 10 МБ не добавлен');
                                setSignupMailNewFiles((prev) => {
                                    const next = prev.concat(allowed);
                                    const kept = (formData.signup_mail_files || []).length;
                                    if (kept + next.length > 5) {
                                        toast.error('Можно прикрепить не больше 5 файлов');
                                        return next.slice(0, Math.max(0, 5 - kept));
                                    }
                                    return next;
                                });
                            }}
                        />
                        <small className="form-help">PDF, Word, Excel, PowerPoint, ZIP или текст, до 5 файлов по 10 МБ. В письме будут ссылки на скачивание.</small>
                        {(formData.signup_mail_files || []).map((file) => (
                            <div key={file.url} className="form-help">
                                {file.name}
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    style={{ marginLeft: 8 }}
                                    onClick={() => setFormData((prev) => ({
                                        ...prev,
                                        signup_mail_files: prev.signup_mail_files.filter((item) => item.url !== file.url)
                                    }))}
                                >
                                    Убрать
                                </button>
                            </div>
                        ))}
                        {signupMailNewFiles.map((file) => (
                            <div key={file.name + file.size} className="form-help">
                                {file.name}
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    style={{ marginLeft: 8 }}
                                    onClick={() => setSignupMailNewFiles((prev) => prev.filter((item) => item !== file))}
                                >
                                    Убрать
                                </button>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {activeTab === 'zoom' && isNasledie && group?.id && (
                <section className="ge-section">
                <h3>Zoom</h3>
                <div className="group-zoom-section">
                    {zoomLoading ? (
                        <p className="access-hint">Загрузка…</p>
                    ) : zoomInfo?.has_zoom && zoomInfo.zoom_url ? (
                        <>
                            <p className="access-hint">Ссылка Zoom для этой группы:</p>
                            {zoomInfo.organizer_code && (
                                <p className="group-zoom-organizer">
                                    <strong>Код организатора:</strong> {zoomInfo.organizer_code}
                                </p>
                            )}
                            {zoomInfo.description && (
                                <pre className="group-zoom-description">{zoomInfo.description}</pre>
                            )}
                            <div className="group-zoom-link-row">
                                <a href={zoomInfo.zoom_url} target="_blank" rel="noopener noreferrer">
                                    {zoomInfo.zoom_url}
                                </a>
                            </div>
                            {zoomInfo.slots?.length > 0 && (
                                <ul className="group-zoom-slots">
                                    {zoomInfo.slots.map((s, i) => (
                                        <li key={i}>
                                            {String(s.date).split('-').reverse().join('.')}
                                            {(s.time_from || s.time_to) && (
                                                <> · {[s.time_from, s.time_to].filter(Boolean).join('–')}</>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                style={{ marginTop: 12 }}
                                disabled={zoomRequesting}
                                onClick={async () => {
                                    setZoomRequesting(true);
                                    try {
                                        await axios.post(
                                            `${API_BASE_URL}/groups/${group.id}/zoom/request`,
                                            {},
                                            { withCredentials: true }
                                        );
                                        toast.success('Ссылка обновлена');
                                        const res = await axios.get(`${API_BASE_URL}/groups/${group.id}/zoom`, {
                                            withCredentials: true
                                        });
                                        setZoomInfo(res.data);
                                    } catch (e) {
                                        toast.error(e.response?.data?.message || 'Не удалось обновить Zoom');
                                    } finally {
                                        setZoomRequesting(false);
                                    }
                                }}
                            >
                                {zoomRequesting ? 'Обновление…' : 'Обновить Zoom'}
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="access-hint">
                                Ссылка Zoom ещё не назначена. Нужно расписание группы с датами — тогда система подберёт свободный канал.
                            </p>
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={zoomRequesting}
                                onClick={async () => {
                                    setZoomRequesting(true);
                                    try {
                                        await axios.post(
                                            `${API_BASE_URL}/groups/${group.id}/zoom/request`,
                                            {},
                                            { withCredentials: true }
                                        );
                                        toast.success('Zoom выдан');
                                        const res = await axios.get(`${API_BASE_URL}/groups/${group.id}/zoom`, {
                                            withCredentials: true
                                        });
                                        setZoomInfo(res.data);
                                    } catch (e) {
                                        toast.error(e.response?.data?.message || 'Не удалось получить Zoom');
                                    } finally {
                                        setZoomRequesting(false);
                                    }
                                }}
                            >
                                {zoomRequesting ? 'Подбор…' : 'Получить Zoom'}
                            </button>
                        </>
                    )}
                </div>
                </section>
            )}

            </form>

            {showEventForm && group?.id && activeTab === 'schedule' && (
                <section className="ge-section">
                    <h3>{editingEvent ? 'Редактировать встречу' : 'Новая встреча'}</h3>
                    <p className="ge-hint">Цикл, даты, тема и тренеры. После сохранения список встреч обновится.</p>
                    <ScheduleForm
                        event={editingEvent}
                        groupId={group.id}
                        onSuccess={() => {
                            queryClient.invalidateQueries(['groupSchedule', group.id]);
                            setShowEventForm(false);
                            setEditingEvent(null);
                            onScheduleChange?.();
                        }}
                        onCancel={() => {
                            setShowEventForm(false);
                            setEditingEvent(null);
                        }}
                    />
                </section>
            )}

            {!showEventForm && (
            <div className="ge-footer">
                <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
                    Отмена
                </button>
                <button type="submit" form="group-edit-form" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Сохранение...' : (group ? 'Сохранить' : 'Создать')}
                </button>
            </div>
            )}
        </div>
    );
};

export default GroupForm;
